import type {
  ComponentDetailsData,
  ComponentDetailsSection,
  DefectDetailsData,
  DefectDetailsSection,
  JobDetailsData,
  JobDetailsSection,
} from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import { CreateUpdateCompositeJobSections, type ExpandableCards } from "@stryker/core/types/jobs";
import type {
  AdditionalJobDetailsData,
  AdditionalJobDetailsSection,
  DesignConditionsData,
  DesignConditionsSection,
} from "@stryker/core/types/sections";
import { create } from "zustand";

export type CUQuoteSections = {
  jobDetails: JobDetailsSection;
  additionalJobDetails: AdditionalJobDetailsSection;
  designConditions: DesignConditionsSection;
  componentDetails: ComponentDetailsSection;
  defectDetails: DefectDetailsSection;
};

const initialDefectDetails = {
  defects: [],
};

const initialState = {
  sections: {
    jobDetails: {
      status: "open",
      isDataFilled: false,
      data: {
        branch: "",
        job_number: null,
        plant_name: "",
        service_name: "",
        design_code: "",
        repair_system: null,
        surface_preparation: null,
      },
    },
    additionalJobDetails: {
      status: "blocked",
      isDataFilled: false,
      addMore: false,
      data: {
        unit: "",
        line_id: null,
      },
    },
    designConditions: {
      status: "blocked",
      isDataFilled: false,
      data: {
        design_pressure: null,
        design_temperature: null,
        unit_for_design_temperature: "",
      },
    },
    componentDetails: {
      status: "blocked",
      isDataFilled: false,
      data: {
        component_options: [],
        component_material: null,
        components_data: [
          {
            component_nps: null,
            component_diameter: null,
            unit_for_component_diameter: null,
            component_thickness: null,
            unit_for_component_thickness: null,
            component_schedule: null,
            is_flanges: false,
          },
        ],
      },
    },
    defectDetails: {
      status: "blocked",
      isDataFilled: false,
      data: initialDefectDetails,
    },
  },
} satisfies { sections: CUQuoteSections };

export type CUQuoteStore = {
  sections: CUQuoteSections;
  setJobDetails: (payload: { data: JobDetailsData; status: ExpandStatus }) => void;
  setAdditionalJobDetails: (payload: { data?: AdditionalJobDetailsData; status: ExpandStatus }) => void;
  setDesignConditions: (payload: { data: DesignConditionsData; status: ExpandStatus }) => void;
  setComponentDetails: (payload: { data: ComponentDetailsData; status: ExpandStatus }) => void;
  setDefectDetails: (payload: { data: DefectDetailsData; status: ExpandStatus }) => void;
  toggleExpandableSection: (section: ExpandableCards) => void;
  resetState: () => void;
  setDefaultValues: (data: CUQuoteSections) => void;
};

const useCreateCustomJobForQuoteStore = create<CUQuoteStore>((set) => ({
  ...initialState,
  setJobDetails: (payload) =>
    set((state) => {
      const shouldReset =
        state.sections.defectDetails.isDataFilled &&
        payload.data.repair_system !== state.sections.jobDetails.data.repair_system;
      const defectDetails: DefectDetailsSection = shouldReset
        ? { ...initialState.sections.defectDetails, status: "canBeOpened" }
        : state.sections.defectDetails;
      const designConditions: DesignConditionsSection =
        payload.data.repair_system !== state.sections.jobDetails.data.repair_system
          ? {
              ...initialState.sections.designConditions,
              status: "canBeOpened",
              data: {
                ...initialState.sections.designConditions.data,
                unit_for_design_temperature: "Fahrenheit",
              },
            }
          : state.sections.designConditions;
      const additionalJobDetails: AdditionalJobDetailsSection =
        state.sections.additionalJobDetails.isDataFilled && state.sections.additionalJobDetails.status !== "blocked"
          ? state.sections.additionalJobDetails
          : {
              ...state.sections.additionalJobDetails,
              status: "open",
            };
      return {
        sections: {
          ...state.sections,
          jobDetails: { data: payload.data, isDataFilled: payload.status === "done", status: payload.status },
          additionalJobDetails,
          defectDetails,
          designConditions,
        },
      };
    }),
  setAdditionalJobDetails: (payload) =>
    set((state) => {
      const designConditions: DesignConditionsSection =
        state.sections.designConditions.isDataFilled && state.sections.designConditions.status !== "blocked"
          ? state.sections.designConditions
          : {
              ...state.sections.designConditions,
              status: "open",
            };
      if (payload.data) {
        return {
          sections: {
            ...state.sections,
            additionalJobDetails: {
              data: payload.data,
              addMore: true,
              isDataFilled: payload.status === "done",
              status: payload.status,
            },
            designConditions,
          },
        };
      }
      return {
        sections: {
          ...state.sections,
          additionalJobDetails: {
            ...state.sections.additionalJobDetails,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          designConditions,
        },
      };
    }),
  setDesignConditions: (payload) =>
    set((state) => {
      const componentDetails: ComponentDetailsSection =
        state.sections.componentDetails.isDataFilled && state.sections.componentDetails.status !== "blocked"
          ? state.sections.componentDetails
          : {
              ...state.sections.componentDetails,
              status: "open",
            };
      return {
        sections: {
          ...state.sections,
          designConditions: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          componentDetails,
        },
      };
    }),
  setComponentDetails: (payload) =>
    set((state) => {
      const shouldChangeDefectDetails =
        state.sections.componentDetails.data.components_data.length !== payload.data.components_data.length;

      const defectDetails: DefectDetailsSection = { ...state.sections.defectDetails };
      if (!defectDetails.isDataFilled || defectDetails.status === "blocked") {
        defectDetails.status = "open";
      }
      if (shouldChangeDefectDetails) {
        defectDetails.status = "open";
        defectDetails.isDataFilled = false;
        defectDetails.data = initialDefectDetails;
      }

      return {
        sections: {
          ...state.sections,
          componentDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
          defectDetails,
        },
      };
    }),
  setDefectDetails: (payload) =>
    set((state) => {
      return {
        sections: {
          ...state.sections,
          defectDetails: {
            data: payload.data,
            isDataFilled: payload.status === "done",
            status: payload.status,
          },
        },
      };
    }),
  toggleExpandableSection: (section) =>
    set((state) => {
      // @ts-expect-error
      const selectedStatus = state.sections[section];
      const previousState = selectedStatus?.status;
      let newSectionStatus = previousState === "open" ? "canBeOpened" : "open";
      if (newSectionStatus === "canBeOpened" && selectedStatus?.isDataFilled) {
        newSectionStatus = "done";
      }
      return {
        sections: {
          ...state.sections,
          [section]: {
            // @ts-expect-error
            ...state.sections[section],
            status: newSectionStatus,
          },
        },
      };
    }),
  setDefaultValues: (payload) =>
    set(() => {
      return { sections: payload };
    }),
  resetState: () =>
    set(() => {
      return initialState;
    }),
}));

export default useCreateCustomJobForQuoteStore;
